import "moment/locale/es";

import moment from "moment";
import React, { createContext, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Badge } from "reactstrap";

import { eventsEndpoints } from "../../api";
import handleError from "../../components/utils/handleError";
import { isPartnerType } from "../../constants/partnerTypes";
import type { RootState } from "../../redux/reducers/session/state";
import getCurrencySymbol from "../../utils/getCurrencySymbol";
import getErrorMessage from "../../utils/getErrorMessage";

interface BotEventDetails {
  realizedLoss?: number | string;
  balance?: number | string;
  contractSize?: number;
  currency?: string;
  minimumRequired?: number | string;
  leverage?: number | string;
  minLeverage?: number | string;
  maxLeverage?: number | string;
  creditAmount?: number | string;
  drawdown?: number | string;
  message?: string;
}

/** Extract BotEventDetails from untyped API data record */
function parseBotEventDetails(data: Record<string, unknown> | null | undefined): BotEventDetails | undefined {
  if (!data) return undefined;
  return {
    realizedLoss:
      typeof data.realizedLoss === "number" || typeof data.realizedLoss === "string" ? data.realizedLoss : undefined,
    balance: typeof data.balance === "number" || typeof data.balance === "string" ? data.balance : undefined,
    contractSize: typeof data.contractSize === "number" ? data.contractSize : undefined,
    currency: typeof data.currency === "string" ? data.currency : undefined,
    minimumRequired:
      typeof data.minimumRequired === "number" || typeof data.minimumRequired === "string"
        ? data.minimumRequired
        : undefined,
    leverage: typeof data.leverage === "number" || typeof data.leverage === "string" ? data.leverage : undefined,
    minLeverage:
      typeof data.minLeverage === "number" || typeof data.minLeverage === "string" ? data.minLeverage : undefined,
    maxLeverage:
      typeof data.maxLeverage === "number" || typeof data.maxLeverage === "string" ? data.maxLeverage : undefined,
    creditAmount:
      typeof data.creditAmount === "number" || typeof data.creditAmount === "string" ? data.creditAmount : undefined,
    drawdown: typeof data.drawdown === "number" || typeof data.drawdown === "string" ? data.drawdown : undefined,
    message: typeof data.message === "string" ? data.message : undefined,
  };
}

interface BotEvent {
  id: string | number;
  type: string;
  details?: BotEventDetails;
  accountName?: string;
  timestamp?: number | string;
  exchangeAccountId?: number | string;
  isRead?: boolean;
}

interface NotificationItem {
  eventId?: string | number;
  isRead?: boolean;
  onClick?: () => void;
  markAsReadTooltip?: string;
  title?: ReactNode;
  description?: ReactNode;
  imgSrc?: string;
  subtitle?: string;
}

interface DashboardNotificationsContextValue {
  notifications: NotificationItem[];
  botEvents: BotEvent[];
  loadNotifications: () => Promise<void>;
  isLoading: boolean;
  markAllAsRead: () => Promise<void>;
  markEventRead: (eventId: string | number) => Promise<void>;
  unreadCount: number;
}

interface DashboardNotificationsWrapperProps {
  children: ReactNode;
}

export const DashboardNotificationsContext = createContext<DashboardNotificationsContextValue>({
  notifications: [],
  botEvents: [],
  loadNotifications: async () => {},
  isLoading: false,
  markAllAsRead: async () => {},
  markEventRead: async () => {},
  unreadCount: 0,
});

const POLLING_INTERVAL = 45000; // 45 seconds

// Event type colors
const eventTypeColors: Record<string, string> = {
  STOP_LOSS: "danger",
  STOP_INSUFFICIENT_FUNDS: "danger",
  STOP_BALANCE_TOO_HIGH: "danger",
  STOP_LEVERAGE_TOO_LOW: "danger",
  STOP_LEVERAGE_TOO_HIGH: "danger",
  STOP_INVALID_CREDENTIALS: "danger",
  STOP_CREDIT_BONUS_RESTRICTED: "danger",
  LOW_MARGIN_WARNING: "warning",
  DRAWDOWN_WARNING: "warning",
};

// Format percentage - if value is decimal (< 1), multiply by 100
const formatPercent = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "--") return "--";
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return "--";
  // If value is less than 1, assume it's a decimal and convert to percentage
  const percent = num < 1 && num > 0 ? num * 100 : num;
  return percent.toFixed(2);
};

const DashboardNotificationsWrapper: React.FC<DashboardNotificationsWrapperProps> = ({ children }) => {
  const { t, i18n } = useTranslation("bots", { keyPrefix: "notifications" });
  const navigate = useNavigate();
  const { user, token } = useSelector((state: RootState) => state.sessionState);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [botEvents, setBotEvents] = useState<BotEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setLoading] = useState<boolean>(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLoadingRef = useRef<boolean>(false);

  // Convert bot event to Header.Session notification format
  const convertEventToNotification = useCallback(
    (event: BotEvent): NotificationItem => {
      const { type, details = {}, accountName, timestamp, exchangeAccountId } = event;
      const color = eventTypeColors[type] || "secondary";
      // timestamp is Unix milliseconds, use current locale
      const eventTime = moment(timestamp).locale(i18n.language);
      // Use accountName (credentials/exchange account name), not configName (strategy)
      const credentialsName = accountName || "Bot";

      // Always use translations (don't use details.message from API as it's in English)
      let description = "";
      switch (type) {
        case "STOP_LOSS":
          description = t("eventMessages.STOP_LOSS", {
            realizedLoss: details.realizedLoss || "--",
            balance: details.balance || "--",
          });
          break;
        case "STOP_INSUFFICIENT_FUNDS": {
          // Determine account type based on contract size
          const accountType = details.contractSize === 100000 ? "standard" : "micro";
          // Default to EUR for old events that don't have currency
          const currencySymbol = getCurrencySymbol(details.currency || "EUR");
          description = t("eventMessages.STOP_INSUFFICIENT_FUNDS", {
            accountType: t(`accountTypes.${accountType}`),
            currencySymbol,
            minimumRequired: details.minimumRequired || "--",
            balance: details.balance || "--",
          });
          break;
        }
        case "STOP_BALANCE_TOO_HIGH":
          description = t("eventMessages.STOP_BALANCE_TOO_HIGH", { balance: details.balance || "--" });
          break;
        case "STOP_LEVERAGE_TOO_LOW":
          description = t("eventMessages.STOP_LEVERAGE_TOO_LOW", {
            leverage: details.leverage || "--",
            minLeverage: details.minLeverage || "--",
          });
          break;
        case "STOP_LEVERAGE_TOO_HIGH":
          description = t("eventMessages.STOP_LEVERAGE_TOO_HIGH", {
            leverage: details.leverage || "--",
            maxLeverage: details.maxLeverage || "--",
          });
          break;
        case "STOP_INVALID_CREDENTIALS":
          description = t("eventMessages.STOP_INVALID_CREDENTIALS");
          break;
        case "STOP_CREDIT_BONUS_RESTRICTED":
          description = t("eventMessages.STOP_CREDIT_BONUS_RESTRICTED", { creditAmount: details.creditAmount || "--" });
          break;
        case "LOW_MARGIN_WARNING":
          description = t("eventMessages.LOW_MARGIN_WARNING");
          break;
        case "DRAWDOWN_WARNING":
          description = t("eventMessages.DRAWDOWN_WARNING", { drawdown: formatPercent(details.drawdown) });
          break;
        default:
          description = type;
      }

      return {
        eventId: event.id,
        isRead: event.isRead,
        onClick: exchangeAccountId ? () => navigate(`/exchange-account/${exchangeAccountId}`) : undefined,
        markAsReadTooltip: t("markAsRead"),
        title: (
          <span className="d-flex align-items-center">
            <Badge color={color} className="mr-2">
              {t(`eventTypes.${type}`, type)}
            </Badge>
            <small className="text-muted">{eventTime.fromNow()}</small>
          </span>
        ),
        description: (
          <span className="font-weight-normal">
            <strong>{credentialsName}</strong> - {description}
          </span>
        ),
      };
    },
    [t, navigate, i18n.language]
  );

  // Fetch bot events (skip for admin users)
  const loadBotEvents = useCallback(async () => {
    if (!token) return { events: [], unreadCount: 0 };

    const isAdmin = user?.role?.name === "Admin";
    // Skip notifications for admin users
    if (isAdmin) return { events: [], unreadCount: 0 };

    try {
      const { data: events } = await eventsEndpoints.getMyEvents({ pagination: { pageSize: 20, page: 1 } });
      // Map api-client Event type to local BotEvent interface
      const botEvents: BotEvent[] = events.map((e) => ({
        id: e.id,
        type: e.type,
        details: parseBotEventDetails(e.details),
        accountName: e.accountName ?? undefined,
        timestamp: e.timestamp,
        exchangeAccountId: e.exchangeAccountId ?? undefined,
        isRead: e.isRead,
      }));
      return {
        events: botEvents,
        unreadCount: botEvents.filter((e) => !e.isRead).length,
      };
    } catch (e) {
      console.error("Error loading bot events:", e);
      throw e; // Re-throw to be handled by loadNotifications
    }
  }, [token, user?.role?.name]);

  const loadCommonNotifications = useCallback(async (): Promise<NotificationItem[]> => {
    const notifications: NotificationItem[] = [];

    // Add onboarding notifications
    if (isPartnerType(user?.referrerType ?? "") && !(user as Record<string, unknown>)?.pm_stripe_finishedOnboarding) {
      try {
        // Onboarding notification code commented out
      } catch (err: unknown) {
        handleError(getErrorMessage(err, "Error while loading onboarding link"), false);
      }
    }
    return notifications;
  }, [user]);

  const loadNotifications = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);

    try {
      // Load common notifications
      const commonNotifications = (await loadCommonNotifications()) || [];

      // Load bot events and convert to notification format
      const { events, unreadCount: apiUnreadCount } = await loadBotEvents();
      setBotEvents(events);
      setUnreadCount(apiUnreadCount);
      const eventNotifications = events.map(convertEventToNotification);

      // Merge all notifications (bot events first, then common)
      setNotifications([...eventNotifications, ...commonNotifications]);
    } catch (e) {
      // On error, keep existing notifications in memory - don't clear them
      console.error("Error loading notifications:", e);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [loadCommonNotifications, loadBotEvents, convertEventToNotification]);

  // Mark a single event as read
  const markEventRead = useCallback(
    async (eventId: string | number) => {
      try {
        await eventsEndpoints.markEventsRead({ eventIds: [Number(eventId)] });
        await loadNotifications(); // Refresh to update read status
      } catch (err: unknown) {
        console.error("Error marking event as read:", err);
      }
    },
    [loadNotifications]
  );

  // Mark all events as read
  const markAllAsRead = useCallback(async () => {
    try {
      await eventsEndpoints.markAllEventsRead();
      await loadNotifications(); // Refresh to update read status
    } catch (err: unknown) {
      console.error("Error marking all as read:", err);
    }
  }, [loadNotifications]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadNotifications();
    }
  }, [user, token, loadNotifications]);

  // Polling for bot events
  useEffect(() => {
    if (!token) return;

    pollingRef.current = setInterval(() => {
      loadNotifications();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [token, loadNotifications]);

  return (
    <DashboardNotificationsContext.Provider
      value={{
        notifications,
        botEvents,
        loadNotifications,
        isLoading,
        markAllAsRead,
        markEventRead,
        unreadCount,
      }}
    >
      {children}
    </DashboardNotificationsContext.Provider>
  );
};
export default DashboardNotificationsWrapper;
